
import React, { useState, useMemo, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import { NIGHT_AUDIT_STEPS, NightAuditStep, NightAuditItem } from './nightAuditData';
import { database, subscribeToPath } from '../../services/database';
import { db } from '../../services/firebase';
import { ref, get } from 'firebase/database';
import { NightAuditReport, StaffMember } from '../../types';
import { toast } from 'react-hot-toast';

interface NightAuditViewProps {
    onBack: () => void;
    staffName: string;
    staffList?: StaffMember[];
}

const NightAuditView: React.FC<NightAuditViewProps> = ({ onBack, staffName, staffList = [] }) => {
    const [activeAudits, setActiveAudits] = useState<{ [id: string]: NightAuditReport }>({});
    const [isAuditActive, setIsAuditActive] = useState(false);
    const [auditSession, setAuditSession] = useState<{
        id: string;
        date: string;
        startTime: string;
        endTime: string;
    } | null>(null);
    const [executorName, setExecutorName] = useState(staffName);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [checkedItems, setCheckedItems] = useState<{ [stepId: number]: boolean[] }>(() => {
        const initial: { [stepId: number]: boolean[] } = {};
        NIGHT_AUDIT_STEPS.forEach(step => {
            initial[step.id] = new Array(step.items.length).fill(false);
        });
        return initial;
    });
    const [showSummary, setShowSummary] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showRestartConfirm, setShowRestartConfirm] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printType, setPrintType] = useState<'summary' | 'detailed' | 'compact'>('summary');

    // Subscribe to active audits
    useEffect(() => {
        const unsubscribe = subscribeToPath('activeNightAudits', (data) => {
            setActiveAudits(data || {});
        });
        return () => unsubscribe();
    }, []);

    const startNewAudit = async (date: string, startTime: string, endTime: string, mode: 'new' | 'resume' | 'override', auditId?: string) => {
        let id = auditId;
        if (mode === 'new') {
            const timestamp = new Date().getTime();
            id = `${date.replace(/-/g, '')}-${startTime.replace(':', '')}-${endTime.replace(':', '')}-${timestamp}`;
        }
        
        const newSession = { id: id!, date, startTime, endTime };
        
        if (mode === 'resume' && id && activeAudits[id]) {
            // Resume existing
            const existing = activeAudits[id];
            const restoredChecked: { [stepId: number]: boolean[] } = {};
            (existing.steps || []).forEach(step => {
                restoredChecked[step.id] = (step.items || []).map(item => item.isDone);
            });
            setCheckedItems(restoredChecked);
            if (existing.fastReportData) {
                const normalized = { ...existing.fastReportData };
                if (typeof normalized.ullivikNoShowAssigned === 'string') {
                    normalized.ullivikNoShowAssigned = { count: normalized.ullivikNoShowAssigned || '0', details: '' };
                }
                if (typeof normalized.ullivikNoShowKeep === 'string') {
                    normalized.ullivikNoShowKeep = { count: normalized.ullivikNoShowKeep || '0', details: '' };
                }
                if (typeof normalized.regularNoShow === 'string') {
                    normalized.regularNoShow = { count: normalized.regularNoShow || '0', details: '' };
                }
                setFastReportData(normalized as any);
            }
            if (typeof existing.currentStepIndex === 'number') {
                setCurrentStepIndex(existing.currentStepIndex);
            }
        } else {
            // New or Override (Reset everything)
            const initial: { [stepId: number]: boolean[] } = {};
            NIGHT_AUDIT_STEPS.forEach(step => {
                initial[step.id] = new Array(step.items.length).fill(false);
            });
            setCheckedItems(initial);
            setCurrentStepIndex(0);
            
            // If override, we keep the ID, so auto-save will eventually overwrite the previous draft.
            // We can also reset fastReportData to default if it's an override or new
            setFastReportData({
                date: (() => {
                    const yest = new Date();
                    yest.setDate(yest.getDate() - 1);
                    return yest.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                        .replace(',', '')
                        .replace(/(\w+), (\w+ \d+), (\d+)/, '$1, $2 - $3');
                })(),
                inHouseRooms: '',
                compRooms: '',
                ullivikNoShowAssigned: { count: '0', details: '' },
                ullivikNoShowKeep: { count: '0', details: '' },
                regularNoShow: { count: '0', details: '' },
                changeRoomsFrom: '',
                changeRoomsTo: ''
            });
        }
        
        setAuditSession(newSession);
        setIsAuditActive(true);
    };

    // Fast Report Data
    const [fastReportData, setFastReportData] = useState({
        date: (() => {
            const yest = new Date();
            yest.setDate(yest.getDate() - 1);
            return yest.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                .replace(',', '')
                .replace(/(\w+), (\w+ \d+), (\d+)/, '$1, $2 - $3');
        })(),
        inHouseRooms: '',
        compRooms: '',
        ullivikNoShowAssigned: { count: '0', details: '' },
        ullivikNoShowKeep: { count: '0', details: '' },
        regularNoShow: { count: '0', details: '' },
        changeRoomsFrom: '',
        changeRoomsTo: ''
    });

    const totalTasks = useMemo(() => NIGHT_AUDIT_STEPS.reduce((sum, step) => sum + step.items.length, 0), []);
    const completedTasks = useMemo(() => {
        return Object.values(checkedItems).reduce((sum, stepChecked) => sum + stepChecked.filter(Boolean).length, 0);
    }, [checkedItems]);
    const overallPercentage = Math.round((completedTasks / totalTasks) * 100);

    // Auto-save logic
    useEffect(() => {
        if (!isAuditActive || !auditSession) return;

        const timer = setTimeout(async () => {
            const auditDraft: Partial<NightAuditReport> = {
                id: auditSession.id,
                date: auditSession.date,
                startTime: auditSession.startTime,
                endTime: auditSession.endTime,
                status: 'in-progress',
                completedBy: executorName,
                currentStepIndex,
                totalTasks,
                completedTasks,
                completionPercentage: overallPercentage,
                steps: NIGHT_AUDIT_STEPS.map(step => ({
                    id: step.id,
                    title: step.title,
                    completedCount: getStepCompletedCount(step.id),
                    totalCount: (step.items?.length || 0),
                    items: (step.items || []).map((item, idx) => ({
                        label: item.l,
                        isDone: (checkedItems[step.id] || [])[idx] || false,
                        note: item.n,
                        subs: item.subs
                    }))
                })),
                fastReportData
            };
            await database.saveActiveNightAudit(auditDraft);
        }, 1500);

        return () => clearTimeout(timer);
    }, [checkedItems, fastReportData, auditSession, isAuditActive, totalTasks, completedTasks, overallPercentage, staffName]);

    const isStepCompleted = (stepId: number) => {
        const step = NIGHT_AUDIT_STEPS.find(s => s.id === stepId);
        if (!step) return false;
        return (checkedItems[stepId] || []).every(Boolean);
    };

    const getStepCompletedCount = (stepId: number) => {
        return (checkedItems[stepId] || []).filter(Boolean).length;
    };

    const toggleItem = (stepId: number, itemIndex: number) => {
        setCheckedItems(prev => ({
            ...prev,
            [stepId]: (prev[stepId] || []).map((val, idx) => idx === itemIndex ? !val : val)
        }));
    };

    const handleRestart = async () => {
        if (auditSession?.id) {
            await database.deleteActiveNightAudit(auditSession.id);
        }
        setIsAuditActive(false);
        setAuditSession(null);
        setShowRestartConfirm(false);
    };

    const handleSaveReport = async () => {
        if (!auditSession) return;
        setIsSaving(true);
        try {
            const report: Partial<NightAuditReport> = {
                id: auditSession.id,
                date: auditSession.date,
                startTime: auditSession.startTime,
                endTime: auditSession.endTime,
                status: 'completed',
                completedAt: new Date().toISOString(),
                completedBy: executorName,
                totalTasks,
                completedTasks,
                completionPercentage: overallPercentage,
                steps: NIGHT_AUDIT_STEPS.map(step => ({
                    id: step.id,
                    title: step.title,
                    completedCount: getStepCompletedCount(step.id),
                    totalCount: (step.items?.length || 0),
                    items: (step.items || []).map((item, idx) => ({
                        label: item.l,
                        isDone: (checkedItems[step.id] || [])[idx] || false,
                        note: item.n,
                        subs: item.subs
                    }))
                })),
                fastReportData
            };

            await database.saveNightAuditReport(report);
            await database.deleteActiveNightAudit(auditSession.id);

            // Also sync with TaskInstances if exists
            try {
                const instancesSnapshot = await get(ref(db, 'taskInstances'));
                if (instancesSnapshot.exists()) {
                    const instances = instancesSnapshot.val();
                    const taskInstance = Object.values(instances).find((i: any) => 
                        i.date === auditSession.date && i.title === 'Night Audit'
                    ) as any;
                    if (taskInstance) {
                        await database.updateTaskInstance(taskInstance.id, {
                            status: 'completed',
                            completedAt: new Date().toISOString(),
                            completedBy: executorName,
                            subTasks: (taskInstance.subTasks || []).map((st: any) => ({ ...st, isDone: true }))
                        });
                    }
                }
            } catch (syncErr) {
                console.warn('Failed to sync with taskInstances:', syncErr);
            }

            toast.success('Night Audit report saved successfully');
            
            // Reset for a fresh start next time
            setIsAuditActive(false);
            setAuditSession(null);
            onBack();
        } catch (error) {
            console.error('Error saving night audit report:', error);
            toast.error('Failed to save report');
        } finally {
            setIsSaving(false);
        }
    };

    const copyFastReport = () => {
        const lines = [
            `Date: ${fastReportData.date}`,
            `No. of in-house rooms including Day Use: ${fastReportData.inHouseRooms}`,
            `No. of complimentary rooms: ${fastReportData.compRooms}`,
            `No. of Ullivik No show assigned names: ${fastReportData.ullivikNoShowAssigned.count} - ${fastReportData.ullivikNoShowAssigned.details}`,
            `No. of Keep for Ullivik No show: ${fastReportData.ullivikNoShowKeep.count} - ${fastReportData.ullivikNoShowKeep.details}`,
            `Regular guest no-show rooms: ${fastReportData.regularNoShow.count} - ${fastReportData.regularNoShow.details}`,
            `Change rooms: from room ${fastReportData.changeRoomsFrom} to room ${fastReportData.changeRoomsTo}`
        ];
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            toast.success('Fast Report copied to clipboard');
        });
    };

    const executePrint = () => {
        const date = new Date().toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        const time = new Date().toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
        
        const win = window.open('', '_blank');
        if (!win) return;

        let bodyHTML = '';
        if (printType === 'summary') {
            bodyHTML = `
                <div style="display: flex; flex-direction: column; gap: 8px;">
                    ${NIGHT_AUDIT_STEPS.map(step => {
                        const done = isStepCompleted(step.id);
                        const count = getStepCompletedCount(step.id);
                        return `
                            <div style="display: flex; align-items: center; gap: 12px; background: #f9f9f7; border: 1px solid #e0e0dc; border-radius: 8px; padding: 10px 14px;">
                                <div style="width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; background: ${done ? '#eaf6f0' : '#fdf6e3'}; color: ${done ? '#3a7d5e' : '#b8870a'}; border: 1px solid ${done ? '#aaddc6' : '#f0d080'};">
                                    ${done ? '✓' : step.id}
                                </div>
                                <div style="flex: 1; font-size: 13px; font-weight: 500;">${step.title}</div>
                                <div style="font-size: 12px; font-weight: 600; color: ${done ? '#3a7d5e' : '#b8870a'};">${count}/${step.items?.length || 0}</div>
                            </div>
                        `;
                    }).join('')}
                </div>
            `;
        } else if (printType === 'compact') {
            bodyHTML = `
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                    ${NIGHT_AUDIT_STEPS.map(step => `
                        <div style="margin-bottom: 8px; break-inside: avoid; border: 1px solid #eee; padding: 4px; rounded: 4px;">
                            <div style="font-size: 10px; font-weight: 800; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 1px; margin-bottom: 4px; display: flex; justify-content: space-between; background: #f9f9f9;">
                                <span>${step.id}. ${step.title}</span>
                                <span style="font-size: 9px;">____ / ${step.items?.length || 0}</span>
                            </div>
                            <div style="display: grid; grid-template-columns: 1fr; gap: 1px;">
                                ${(step.items || []).map((item, idx) => `
                                    <div style="display: flex; align-items: flex-start; gap: 4px; font-size: 9px; line-height: 1.1;">
                                        <div style="width: 8px; height: 8px; border: 1px solid #000; margin-top: 1px; flex-shrink: 0;"></div>
                                        <div style="flex: 1;">
                                            <span style="font-weight: 600;">${idx + 1}.</span> ${item.l}
                                            ${item.subs ? `
                                                <div style="margin-top: 0px; padding-left: 6px; border-left: 1px solid #eee; display: flex; flex-direction: column; gap: 0px;">
                                                    ${item.subs.map((sub, sIdx) => `<div style="font-size: 8px; color: #666;">• ${sub}</div>`).join('')}
                                                </div>
                                            ` : ''}
                                        </div>
                                    </div>
                                `).join('')}
                            </div>
                        </div>
                    `).join('')}
                </div>
            `;
        } else {
            bodyHTML = NIGHT_AUDIT_STEPS.map(step => {
                const done = isStepCompleted(step.id);
                const count = getStepCompletedCount(step.id);
                return `
                    <div style="margin-bottom: 20px; break-inside: avoid;">
                        <div style="display: flex; align-items: center; gap: 10px; background: #0d1b2a; color: #f0ead8; border-radius: 8px; padding: 10px 15px; margin-bottom: 10px;">
                            <div style="width: 24px; height: 24px; border-radius: 50%; background: #c9a84c; color: #0d1b2a; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center;">${step.id}</div>
                            <div style="flex: 1; font-size: 14px; font-weight: 600;">${step.title}</div>
                            <div style="font-size: 12px; font-weight: 600; color: ${done ? '#4db380' : '#e8c96a'};">${count}/${step.items?.length || 0}</div>
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 5px;">
                            ${(step.items || []).map((item, idx) => {
                                const isDone = (checkedItems[step.id] || [])[idx];
                                return `
                                    <div style="display: flex; align-items: flex-start; gap: 10px; border: 1px solid #e8e8e4; border-radius: 8px; padding: 8px 12px; background: ${isDone ? '#f2fbf7' : '#fff'};">
                                        <div style="width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid ${isDone ? '#4db380' : '#bbb'}; background: ${isDone ? '#4db380' : 'transparent'}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 10px; flex-shrink: 0; margin-top: 2px;">
                                            ${isDone ? '✓' : ''}
                                        </div>
                                        <div style="flex: 1;">
                                            <div style="font-size: 13px; font-weight: 500; color: ${isDone ? '#3a7d5e' : '#111'}; ${isDone ? 'text-decoration: line-through;' : ''}">${idx + 1}. ${item.l}</div>
                                            ${item.n ? `<div style="font-size: 11px; color: #888; margin-top: 4px;">${item.n}</div>` : ''}
                                            ${item.subs ? `
                                                <div style="margin-top: 6px; padding-left: 8px; border-left: 2px solid #e0e0dc; display: flex; flex-direction: column; gap: 3px;">
                                                    ${item.subs.map((sub, sIdx) => `<div style="font-size: 11px; color: #666;"><span style="color: #aaa; font-weight: 600;">${idx + 1}.${sIdx + 1}</span> ${sub}</div>`).join('')}
                                                </div>
                                            ` : ''}
                                        </div>
                                    </div>
                                `;
                            }).join('')}
                        </div>
                    </div>
                `;
            }).join('');
        }

        win.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Night Audit Report — ${date}</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #111; background: #fff; padding: 2cm; font-size: 14px; }
                    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #c9a84c; padding-bottom: 15px; margin-bottom: 20px; }
                    .title { font-size: 20px; font-weight: bold; color: #0d1b2a; }
                    .sub { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; margin-top: 5px; }
                    .date-info { text-align: right; font-size: 12px; color: #888; }
                    .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 25px; }
                    .stat-box { background: #f5f5f2; border: 1px solid #ddd; border-radius: 10px; padding: 15px; text-align: center; }
                    .stat-num { font-size: 24px; font-weight: bold; color: #c9a84c; }
                    .stat-label { font-size: 10px; color: #888; text-transform: uppercase; margin-top: 5px; }
                    @media print { @page { margin: 1.5cm; } body { padding: 0; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <div>
                        <div class="title">Quality Hotel — Night Audit</div>
                        <div class="sub">Shift Completion Report</div>
                    </div>
                    <div class="date-info">
                        <div>${date}</div>
                        <div>Printed at ${time}</div>
                    </div>
                </div>
                <div class="stats">
                    <div class="stat-box"><div class="stat-num">${completedTasks}</div><div class="stat-label">Tasks Done</div></div>
                    <div class="stat-box"><div class="stat-num">${overallPercentage}%</div><div class="stat-label">Completion</div></div>
                    <div class="stat-box"><div class="stat-num">${totalTasks - completedTasks}</div><div class="stat-label">Remaining</div></div>
                </div>
                ${bodyHTML}
                <script>window.onload = () => { window.print(); window.close(); };</script>
            </body>
            </html>
        `);
        win.document.close();
        setShowPrintModal(false);
    };

    const currentStep = NIGHT_AUDIT_STEPS[currentStepIndex];

    if (!isAuditActive) {
        return (
            <div className="max-w-xl mx-auto py-12 px-4">
                <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-slate-700">
                    <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icons.Clock className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white text-center mb-2 uppercase tracking-tight">Setup Night Audit</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-center mb-8 font-medium">
                        Configure the audit session details before starting.
                    </p>

                    <div className="space-y-6">
                        {Object.keys(activeAudits).length > 0 && (
                            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 mb-8">
                                <h3 className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-3">Active Audits Found</h3>
                                <div className="space-y-2">
                                    {Object.values(activeAudits).map(audit => (
                                        <div key={audit.id} className="flex items-center justify-between gap-4 p-3 bg-white dark:bg-slate-800 rounded-xl border border-indigo-100 dark:border-indigo-900/20">
                                            <div>
                                                <div className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-tight">{audit.date}</div>
                                                <div className="text-[10px] text-gray-500 font-bold uppercase">{audit.startTime} – {audit.endTime} ({audit.completionPercentage}% Done)</div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => startNewAudit(audit.date, audit.startTime!, audit.endTime!, 'resume', audit.id)}
                                                    className="px-3 py-1.5 bg-indigo-600 text-white text-[10px] font-black rounded-lg hover:bg-indigo-700 transition-colors uppercase tracking-widest"
                                                >
                                                    Resume
                                                </button>
                                                <button 
                                                    onClick={async () => {
                                                        if (confirm('Are you sure you want to OVERRIDE (clear all progress) for this process?')) {
                                                            await startNewAudit(audit.date, audit.startTime!, audit.endTime!, 'override', audit.id);
                                                            toast.success('Process overridden and reset');
                                                        }
                                                    }}
                                                    className="px-3 py-1.5 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 text-[10px] font-black rounded-lg hover:bg-red-100 transition-colors uppercase tracking-widest"
                                                >
                                                    Override
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-4">Assigned Staff</label>
                                <div className="relative">
                                    <Icons.User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <select 
                                        value={executorName}
                                        onChange={(e) => setExecutorName(e.target.value)}
                                        className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 pl-12 text-sm font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none appearance-none"
                                    >
                                        <option value="">Select Staff Member</option>
                                        {staffList.filter(s => s.status !== 'deleted' && s.status !== 'suspended').sort((a,b) => a.name.localeCompare(b.name)).map(staff => (
                                            <option key={staff.id} value={staff.name}>{staff.name}</option>
                                        ))}
                                        {!staffList.some(s => s.name === staffName) && (
                                            <option value={staffName}>{staffName} (Me)</option>
                                        )}
                                    </select>
                                    <Icons.ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-4">Audit Date</label>
                                <input 
                                    type="date"
                                    defaultValue={new Date().toISOString().split('T')[0]}
                                    id="auditDate"
                                    className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-4">Start Time</label>
                                    <input 
                                        type="time"
                                        defaultValue="23:00"
                                        id="startTime"
                                        className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-4">End Time</label>
                                    <input 
                                        type="time"
                                        defaultValue="07:00"
                                        id="endTime"
                                        className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-2xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        <button 
                            onClick={() => {
                                const dateInput = (document.getElementById('auditDate') as HTMLInputElement);
                                const startInput = (document.getElementById('startTime') as HTMLInputElement);
                                const endInput = (document.getElementById('endTime') as HTMLInputElement);
                                if (!dateInput.value || !startInput.value || !endInput.value) {
                                    toast.error('Please fill in all session details');
                                    return;
                                }
                                startNewAudit(dateInput.value, startInput.value, endInput.value, 'new');
                            }}
                            className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none uppercase tracking-widest"
                        >
                            Start New Audit Process
                        </button>
                        
                        <button 
                            onClick={onBack}
                            className="w-full py-3 text-gray-500 dark:text-gray-400 font-bold hover:text-gray-700 transition-colors text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (showSummary) {
        return (
            <div className="max-w-3xl mx-auto py-8 px-4">
                <button 
                    onClick={() => setShowSummary(false)}
                    className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-bold transition-colors mb-6"
                >
                    <Icons.ArrowLeft className="w-4 h-4" />
                    Back to Audit
                </button>
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-500">
                        <Icons.Check className="w-10 h-10 text-green-600 dark:text-green-400" />
                    </div>
                    <h1 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2">Night Audit Report</h1>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                        Shift summary — {new Date().toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 text-center">
                        <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{completedTasks}</div>
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Tasks Done</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 text-center">
                        <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{overallPercentage}%</div>
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Completion</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 text-center">
                        <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{totalTasks - completedTasks}</div>
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Remaining</div>
                    </div>
                </div>

                <div className="space-y-3 mb-10">
                    {NIGHT_AUDIT_STEPS.map(step => {
                        const done = isStepCompleted(step.id);
                        const count = getStepCompletedCount(step.id);
                        return (
                            <div 
                                key={step.id}
                                className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                                    done 
                                    ? 'bg-green-50/50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30' 
                                    : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700'
                                }`}
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${
                                    done 
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                                    : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                }`}>
                                    {done ? <Icons.Check className="w-4 h-4" /> : step.id}
                                </div>
                                <div className="flex-1 font-bold text-gray-900 dark:text-white">{step.title}</div>
                                <div className={`text-sm font-black ${done ? 'text-green-600' : 'text-indigo-600'}`}>
                                    {count}/{step.items?.length || 0}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex flex-wrap gap-4 justify-center">
                    <button 
                        onClick={() => setShowRestartConfirm(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 font-bold rounded-2xl border border-red-100 dark:border-red-900/30 hover:bg-red-100 transition-colors"
                    >
                        <Icons.RefreshCw className="w-4 h-4" />
                        New Audit
                    </button>
                    <button 
                        onClick={() => setShowPrintModal(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 font-bold rounded-2xl border border-gray-200 dark:border-slate-700 hover:bg-gray-50 transition-colors"
                    >
                        <Icons.Printer className="w-4 h-4" />
                        Print Report
                    </button>
                    <button 
                        onClick={handleSaveReport}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-8 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 dark:shadow-none disabled:opacity-50"
                    >
                        {isSaving ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Save className="w-4 h-4" />}
                        Save to Database
                    </button>
                </div>

                {/* Restart Confirmation Modal */}
                {showRestartConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100 dark:border-slate-700">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Icons.AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
                            </div>
                            <h3 className="text-xl font-black text-gray-900 dark:text-white text-center mb-2 uppercase tracking-tight">Start New Audit?</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-center mb-8 font-medium">
                                This will clear all completed tasks and reset every step. This cannot be undone.
                            </p>
                            <div className="flex gap-3">
                                <button 
                                    onClick={() => setShowRestartConfirm(false)}
                                    className="flex-1 py-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 font-bold rounded-2xl hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleRestart}
                                    className="flex-1 py-3 bg-red-600 text-white font-bold rounded-2xl hover:bg-red-700 transition-colors"
                                >
                                    Yes, Restart
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Print Modal */}
                {showPrintModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100 dark:border-slate-700">
                            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Icons.Printer className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <h3 className="text-xl font-black text-gray-900 dark:text-white text-center mb-2 uppercase tracking-tight">Print Report</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-center mb-8 font-medium">
                                Choose which version to print.
                            </p>
                            <div className="space-y-3 mb-8">
                                <button 
                                    onClick={() => setPrintType('summary')}
                                    className={`w-full p-4 rounded-2xl border text-left transition-all ${
                                        printType === 'summary' 
                                        ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20' 
                                        : 'border-gray-100 dark:border-slate-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="font-bold text-gray-900 dark:text-white">Summary</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Step names and completion counts only</div>
                                </button>
                                <button 
                                    onClick={() => setPrintType('detailed')}
                                    className={`w-full p-4 rounded-2xl border text-left transition-all ${
                                        printType === 'detailed' 
                                        ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20' 
                                        : 'border-gray-100 dark:border-slate-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="font-bold text-gray-900 dark:text-white">Detailed</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">All steps with full task and sub-step details</div>
                                </button>
                                <button 
                                    onClick={() => setPrintType('compact')}
                                    className={`w-full p-4 rounded-2xl border text-left transition-all ${
                                        printType === 'compact' 
                                        ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20' 
                                        : 'border-gray-100 dark:border-slate-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="font-bold text-gray-900 dark:text-white">Compact Checklist</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Tiny spaces, perfect for a physical check-off sheet</div>
                                </button>
                            </div>
                            <div className="flex gap-3">
                                <button 
                                    onClick={() => setShowPrintModal(false)}
                                    className="flex-1 py-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 font-bold rounded-2xl hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={executePrint}
                                    className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-colors"
                                >
                                    Print
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-8 px-4">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={onBack}
                            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-bold transition-colors"
                        >
                            <Icons.ArrowLeft className="w-4 h-4" />
                            Back to Tasks
                        </button>
                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-slate-800 p-1 rounded-xl border border-gray-200 dark:border-slate-700">
                            <button 
                                onClick={() => {
                                    setPrintType('compact');
                                    setShowPrintModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 font-bold rounded-lg shadow-sm hover:bg-indigo-50 transition-all text-xs"
                            >
                                <Icons.Printer className="w-3.5 h-3.5" />
                                Print Checklist
                            </button>
                            <button 
                                onClick={() => {
                                    setPrintType('detailed');
                                    setShowPrintModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-indigo-600 font-bold transition-colors text-xs"
                            >
                                Detailed
                            </button>
                            <button 
                                onClick={() => {
                                    setPrintType('summary');
                                    setShowPrintModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-indigo-600 font-bold transition-colors text-xs"
                            >
                                Summary
                            </button>
                        </div>
                    </div>
                    <button 
                        onClick={() => setShowSummary(true)}
                        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-bold transition-colors"
                    >
                        View Summary
                        <Icons.ArrowRight className="w-4 h-4" />
                    </button>
                </div>
                
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-black text-[10px] uppercase tracking-[0.2em] mb-2">
                            <div className="w-8 h-[1px] bg-indigo-600 dark:bg-indigo-400 opacity-50"></div>
                            Step {currentStep.id} of {NIGHT_AUDIT_STEPS.length}
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2">{currentStep.title}</h1>
                        <p className="text-gray-500 dark:text-gray-400 font-medium max-w-xl leading-relaxed">
                            {currentStep.desc}
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{overallPercentage}%</div>
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Overall Progress</div>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="flex gap-1 mb-8">
                {NIGHT_AUDIT_STEPS.map((step, idx) => {
                    const isDone = isStepCompleted(step.id);
                    const isActive = idx === currentStepIndex;
                    const stepPct = Math.round(getStepCompletedCount(step.id) / (step.items?.length || 1) * 100);
                    
                    return (
                        <div key={step.id} className="flex-1 h-1.5 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div 
                                className={`h-full transition-all duration-500 ${isDone ? 'bg-green-500' : 'bg-indigo-600'}`}
                                style={{ width: isDone ? '100% ' : isActive ? `${stepPct}%` : idx < currentStepIndex ? '100%' : '0%' }}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Task List */}
            <div className="space-y-4 mb-10">
                <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">
                    <span className="text-indigo-600 dark:text-indigo-400">{getStepCompletedCount(currentStep.id)}</span> of {currentStep.items?.length || 0} tasks completed
                </div>
                
                {(currentStep.items || []).map((item, idx) => {
                    const isDone = (checkedItems[currentStep.id] || [])[idx];
                    
                    return (
                        <div 
                            key={idx}
                            onClick={() => toggleItem(currentStep.id, idx)}
                            className={`group relative bg-white dark:bg-slate-800 p-5 rounded-3xl shadow-sm border transition-all cursor-pointer ${
                                isDone 
                                ? 'border-green-100 dark:border-green-900/30 bg-green-50/30 dark:bg-green-900/10' 
                                : 'border-gray-100 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-900/50'
                            }`}
                        >
                            <div className="flex gap-5">
                                <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center transition-all ${
                                    isDone 
                                    ? 'bg-green-500 border-green-500' 
                                    : 'border-gray-200 dark:border-slate-600 group-hover:border-indigo-400'
                                }`}>
                                    {isDone && <Icons.Check className="w-3.5 h-3.5 text-white" />}
                                </div>
                                <div className="flex-1">
                                    <div className={`font-bold text-gray-900 dark:text-white leading-tight mb-1 ${isDone ? 'line-through text-gray-400 dark:text-gray-500' : ''}`}>
                                        <span className="text-indigo-600 dark:text-indigo-400 mr-1">{idx + 1}.</span> {item.l}
                                    </div>
                                    {item.n && (
                                        <p className={`text-sm font-medium leading-relaxed ${isDone ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                                            {item.n}
                                        </p>
                                    )}

                                    {item.subs && (
                                        <div 
                                            className={`mt-4 pl-4 border-l-2 space-y-3 ${isDone ? 'border-green-200 dark:border-green-900/30' : 'border-gray-100 dark:border-slate-700'}`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {item.copy && (
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sub-steps</span>
                                                    <button 
                                                        onClick={copyFastReport}
                                                        className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-black rounded-lg hover:bg-indigo-100 transition-colors uppercase tracking-widest"
                                                    >
                                                        <Icons.Copy className="w-3 h-3" />
                                                        Copy Report
                                                    </button>
                                                </div>
                                            )}
                                            {item.subs.map((sub, sIdx) => {
                                                const num = `${idx + 1}.${sIdx + 1}`;
                                                
                                                // Special handling for editable fields in Fast Report
                                                if (item.copy && sIdx === 0) {
                                                    return (
                                                        <div key={sIdx} className="flex items-center gap-3">
                                                            <span className="text-xs font-bold text-gray-400">{num}</span>
                                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Date —</span>
                                                            <input 
                                                                type="text"
                                                                value={fastReportData.date}
                                                                onChange={e => setFastReportData(prev => ({ ...prev, date: e.target.value }))}
                                                                className="flex-1 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                                            />
                                                        </div>
                                                    );
                                                }

                                                // Mapping other fields for Fast Report
                                                const fieldMap: { [key: number]: string } = {
                                                    1: 'inHouseRooms',
                                                    2: 'compRooms',
                                                    3: 'ullivikNoShowAssigned',
                                                    4: 'ullivikNoShowKeep',
                                                    5: 'regularNoShow'
                                                };

                                                if (item.copy && fieldMap[sIdx]) {
                                                    const fieldName = fieldMap[sIdx];
                                                    const isDoubleField = fieldName === 'ullivikNoShowAssigned' || fieldName === 'ullivikNoShowKeep' || fieldName === 'regularNoShow';

                                                    if (isDoubleField) {
                                                        const data = fastReportData[fieldName as 'ullivikNoShowAssigned' | 'ullivikNoShowKeep' | 'regularNoShow'];
                                                        return (
                                                            <div key={sIdx} className="flex flex-wrap items-center gap-3">
                                                                <span className="text-xs font-bold text-gray-400">{num}</span>
                                                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{sub} —</span>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Count</span>
                                                                        <input 
                                                                            type="text"
                                                                            value={data.count}
                                                                            onChange={e => setFastReportData(prev => ({ 
                                                                                ...prev, 
                                                                                [fieldName]: { ...prev[fieldName as 'ullivikNoShowAssigned' | 'ullivikNoShowKeep' | 'regularNoShow'], count: e.target.value } 
                                                                            }))}
                                                                            className="w-16 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-900 dark:text-white text-center"
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col flex-1 min-w-[200px]">
                                                                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Details (Room numbers)</span>
                                                                        <input 
                                                                            type="text"
                                                                            value={data.details}
                                                                            onChange={e => setFastReportData(prev => ({ 
                                                                                ...prev, 
                                                                                [fieldName]: { ...prev[fieldName as 'ullivikNoShowAssigned' | 'ullivikNoShowKeep' | 'regularNoShow'], details: e.target.value } 
                                                                            }))}
                                                                            className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-900 dark:text-white"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div key={sIdx} className="flex items-center gap-3">
                                                            <span className="text-xs font-bold text-gray-400">{num}</span>
                                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{sub} —</span>
                                                            <input 
                                                                type="text"
                                                                value={fastReportData[fieldName as keyof typeof fastReportData] as string}
                                                                onChange={e => setFastReportData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                                                className="flex-1 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                                            />
                                                        </div>
                                                    );
                                                }

                                                // Change rooms field
                                                if (item.copy && sIdx === item.subs.length - 1) {
                                                    return (
                                                        <div key={sIdx} className="flex flex-wrap items-center gap-3">
                                                            <span className="text-xs font-bold text-gray-400">{num}</span>
                                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Change rooms —</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase">From</span>
                                                                <input 
                                                                    type="text"
                                                                    maxLength={6}
                                                                    value={fastReportData.changeRoomsFrom}
                                                                    onChange={e => setFastReportData(prev => ({ ...prev, changeRoomsFrom: e.target.value }))}
                                                                    className="w-16 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-900 dark:text-white text-center"
                                                                />
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase">To</span>
                                                                <input 
                                                                    type="text"
                                                                    maxLength={6}
                                                                    value={fastReportData.changeRoomsTo}
                                                                    onChange={e => setFastReportData(prev => ({ ...prev, changeRoomsTo: e.target.value }))}
                                                                    className="w-16 bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-900 dark:text-white text-center"
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={sIdx} className="flex items-start gap-2.5 py-0.5 group/sub">
                                                        <div className="mt-0.5 opacity-40 group-hover/sub:opacity-100 transition-opacity">
                                                            <Icons.ChevronRight className="w-3.5 h-3.5 text-indigo-500" />
                                                        </div>
                                                        <p className={`text-xs font-medium leading-relaxed ${isDone ? 'text-gray-400 dark:text-gray-600' : 'text-gray-600 dark:text-gray-400'}`}>
                                                            {sub}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-8 border-t border-gray-100 dark:border-slate-700">
                <button 
                    onClick={() => setCurrentStepIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentStepIndex === 0}
                    className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 font-bold rounded-2xl border border-gray-200 dark:border-slate-700 hover:bg-gray-50 transition-colors disabled:opacity-30"
                >
                    <Icons.ChevronLeft className="w-4 h-4" />
                    Previous
                </button>
                
                {currentStepIndex === NIGHT_AUDIT_STEPS.length - 1 ? (
                    <button 
                        onClick={() => setShowSummary(true)}
                        className="flex items-center gap-2 px-8 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 dark:shadow-none"
                    >
                        View Report
                        <Icons.ArrowRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button 
                        onClick={() => setCurrentStepIndex(prev => Math.min(NIGHT_AUDIT_STEPS.length - 1, prev + 1))}
                        className="flex items-center gap-2 px-8 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 dark:shadow-none"
                    >
                        Next Step
                        <Icons.ChevronRight className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default NightAuditView;
